import { useCallback, useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import store, { AllState } from '../../store'
import {
  changeGanttParameter,
  failedToReceiveProjectGanttParameter,
  failedToReceiveRootGanttParameter,
  getGanttParameter,
  getProjectGanttParameter,
  setGanttDisplayRangeMode,
  receivedProjectGanttParameter,
  receivedRootGanttParameter,
} from '../../store/project'
import {
  DEFAULT_GANTT_DISPLAY_RANGE_MODE,
  GanttDisplayRangeMode,
  GanttParameterVO,
} from '../../domain/value-object/GanttParameterVO'
import {
  getUserProjectGanttSetting,
  updateUserProjectGanttSetting,
} from '../../infrastructure/api/user-project-gantt-settings'

/** プロジェクト単位の表示設定を読み込み、このフックの呼び出し後に読み込みが完了したかを返す */
export const useIsProjectGanttParameterLoaded = (
  projectUuid: string | undefined
): boolean => {
  const [loadedProjectUuid, setLoadedProjectUuid] = useState<string>()
  useEffect(() => {
    if (!projectUuid) return
    let disposed = false
    getProjectGanttParameter(projectUuid)
      .then(saved => {
        if (disposed) return
        store.dispatch(receivedProjectGanttParameter(saved))
        setLoadedProjectUuid(projectUuid)
      })
      .catch(() => {
        if (disposed) return
        store.dispatch(failedToReceiveProjectGanttParameter())
        setLoadedProjectUuid(projectUuid)
      })
    return () => {
      disposed = true
    }
  }, [projectUuid])
  return !!projectUuid && loadedProjectUuid === projectUuid
}

/** 利用者×プロジェクトの表示期間の動作を読み込み、このフックの呼び出し後に読み込みが完了したかを返す */
export const useIsGanttDisplayRangeModeLoaded = (
  projectUuid: string | undefined
): boolean => {
  const [loadedProjectUuid, setLoadedProjectUuid] = useState<string>()
  const displayRangeMode = useSelector(
    (state: AllState) => state.project.displayRangeMode
  )
  useEffect(() => {
    if (!projectUuid) return
    let disposed = false
    getUserProjectGanttSetting(projectUuid)
      .then(setting => {
        if (disposed) return
        store.dispatch(setGanttDisplayRangeMode(setting.displayRangeMode))
        setLoadedProjectUuid(projectUuid)
      })
      .catch(() => {
        if (disposed) return
        store.dispatch(
          setGanttDisplayRangeMode(DEFAULT_GANTT_DISPLAY_RANGE_MODE)
        )
        setLoadedProjectUuid(projectUuid)
      })
    return () => {
      disposed = true
    }
  }, [projectUuid])
  return (
    !!projectUuid &&
    loadedProjectUuid === projectUuid &&
    displayRangeMode !== undefined
  )
}

/** 表示期間の動作を切り替える。store を先に更新し、保存の応答を待たない */
export const useChangeGanttDisplayRangeMode = () =>
  useCallback(
    (
      projectUuid: string,
      mode: GanttDisplayRangeMode,
      displayed: GanttParameterVO
    ) => {
      if (mode === GanttDisplayRangeMode.CONSTANT) {
        // 表示中の単位と表示期間を一定の値として保存し、切り替えても表示を変えない
        store.dispatch(changeGanttParameter(projectUuid, displayed))
      }
      store.dispatch(setGanttDisplayRangeMode(mode))
      updateUserProjectGanttSetting(projectUuid, mode)
    },
    []
  )

/**
 * 表示設定をルートごとに読み書きするときのルート。
 * 一定のとき、および表示期間の動作の読み込み前は undefined で、プロジェクト単位の表示設定を使う。
 */
export const useGanttParameterRootUuid = (
  rootProjectPlanUuid: string | undefined
): string | undefined => {
  const isRememberRange = useSelector(
    (state: AllState) =>
      state.project.displayRangeMode === GanttDisplayRangeMode.REMEMBER
  )
  return isRememberRange ? rootProjectPlanUuid : undefined
}

export const useRootGanttParameter = (
  projectUuid: string | undefined,
  rootProjectPlanUuid: string | undefined
) => {
  useEffect(() => {
    if (!projectUuid || !rootProjectPlanUuid) {
      store.dispatch(receivedRootGanttParameter(undefined))
      return
    }
    let disposed = false
    getGanttParameter(projectUuid, rootProjectPlanUuid)
      .then(parameter => {
        if (disposed) return
        store.dispatch(
          receivedRootGanttParameter(parameter, rootProjectPlanUuid)
        )
      })
      .catch(() => {
        if (disposed) return
        store.dispatch(failedToReceiveRootGanttParameter(rootProjectPlanUuid))
      })
    return () => {
      disposed = true
    }
  }, [projectUuid, rootProjectPlanUuid])

  // 読み込み結果を画面の外に残すと、次に開いたときに古い結果で初回表示の要否を判定するため、離れるときに消す
  useEffect(
    () => () => {
      store.dispatch(receivedRootGanttParameter(undefined))
    },
    []
  )
}
